import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import useToasts from '../components/useToasts';

interface LoginPageProps {
  onLoginSuccess: (token: string, email: string, role: string) => void;
  isAuthenticated: boolean;
}

const LoginPage = ({ onLoginSuccess, isAuthenticated }: LoginPageProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToasts();

  const extractRoleFromToken = (token: string) => {
    try {
      const [, payload] = token.split('.');
      if (!payload) return [];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized));
      const rawRoles = decoded?.roles ?? decoded?.role ?? decoded?.permissions ?? decoded?.permission;
      const rolesArray = Array.isArray(rawRoles)
        ? rawRoles
        : typeof rawRoles === 'string'
          ? rawRoles.split(/[,\s]+/)
          : [];
      return rolesArray
        .map((role) => (typeof role === 'string' ? role.toUpperCase() : ''))
        .filter(Boolean);
    } catch (error) {
      console.error('Falha ao ler roles do token:', error);
      return [];
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowAlert(false);
    setErrorMessage('');

    if (!email.trim() || !password.trim()) {
      const message = 'Preencha todos os campos';
      setErrorMessage(message);
      setShowAlert(true);
      showToast({ title: 'Atenção', description: message, status: 'warning' });
      return;
    }

    setIsLoading(true);

    const apiBaseUrl =
      process.env.NODE_ENV === 'development'
        ? '/api/v1'
        : 'https://newapi.chippix.com.br/v1';

    try {
      const response = await fetch(`${apiBaseUrl}/user/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          liveManager: true,
          role: 'CLUB',
        }),
      });

      if (!response.ok) {
        let message = 'Não foi possível realizar o login. Verifique suas credenciais.';
        try {
          const errorBody = await response.json();
          if (errorBody?.message) {
            message = errorBody.message;
          }
        } catch (parseError) {
          // ignora falhas de parse
        }
        throw new Error(message);
      }

      const data = await response.json();
      const sessionToken: string | undefined =
        data?.token ||
        data?.access_token ||
        data?.accessToken ||
        data?.data?.token ||
        data?.data?.access_token ||
        data?.data?.accessToken;

      if (!sessionToken) {
        throw new Error('Resposta inválida do servidor: token não encontrado.');
      }

      const roles = extractRoleFromToken(sessionToken);
      const allowedRole = roles.find((role) => role === 'LIVE' || role === 'LIVE_OPERATOR');

      if (!allowedRole) {
        throw new Error('Seu usuário não possui permissão LIVE para acessar este painel.');
      }

      onLoginSuccess(sessionToken, email, allowedRole);
      setEmail('');
      setPassword('');
      showToast({ title: 'Bem-vindo', description: 'Login realizado com sucesso.', status: 'success' });
      navigate('/home');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro inesperado ao tentar realizar o login.';
      setErrorMessage(message);
      setShowAlert(true);
      showToast({ title: 'Falha no login', description: message, status: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      px={{ base: 4, md: 6 }}
      bgGradient="linear(to-br, gray.100, white)"
      _dark={{ bgGradient: 'linear(to-br, gray.900, gray.800)' }}
    >
      <Box w="full" maxW={{ base: 'lg', md: 'xl' }}>
        <Stack spacing={6} textAlign="center" mb={{ base: 6, md: 8 }}>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            letterSpacing="wide"
            color="brand.500"
            textTransform="uppercase"
          >
            Chippix Club Portal
          </Text>
          <Heading size="lg">Entre para gerenciar a wallet dos jogadores</Heading>
          <Text color="text.600">
            Acesso seguro para consultar e acompanhar os dados de saldo e conta de jogo em tempo real.
          </Text>
        </Stack>

        <Box
          as="form"
          onSubmit={handleSubmit}
          bg="surface"
          border="1px solid"
          borderColor="gray.100"
          _dark={{ borderColor: 'gray.700' }}
          rounded="2xl"
          boxShadow="sm"
          px={{ base: 6, md: 10 }}
          py={{ base: 8, md: 10 }}
        >
          <Stack spacing={6}>
            {showAlert && (
              <Alert status="error" rounded="lg" alignItems="flex-start">
                <AlertIcon mt={1} />
                <Box flex="1" mr={4}>
                  <AlertTitle fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                    Falha no login
                  </AlertTitle>
                  <AlertDescription mt={1}>{errorMessage}</AlertDescription>
                </Box>
                <CloseButton
                  position="relative"
                  top={1}
                  onClick={() => setShowAlert(false)}
                />
              </Alert>
            )}

            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  placeholder="nome@dominio.com"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (showAlert) {
                      setShowAlert(false);
                    }
                  }}
                  size="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Senha</FormLabel>
                <Input
                  type="password"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (showAlert) {
                      setShowAlert(false);
                    }
                  }}
                  size="lg"
                />
              </FormControl>
            </Stack>

            <Button
              type="submit"
              size="lg"
              colorScheme="brand"
              isLoading={isLoading}
              loadingText="Entrando..."
              w="full"
            >
              Entrar
            </Button>
          </Stack>
        </Box>

        <Text
          mt={6}
          textAlign="center"
          fontSize="sm"
          color="text.600"
        >
          Precisa de ajuda? Fale com o suporte Chippix Clube para recuperar seu acesso.
        </Text>
      </Box>
    </Flex>
  );
};

export default LoginPage;
